/**
 * Training (#190): what a bar teaches its own staff, and how far each of
 * them has got.
 *
 * A **module** is written by the bar, about the bar: how we open, how we
 * greet, what the list is for, how a Negroni goes out. It is a run of
 * **pages** a trainee works through in order, then a **check** — a few
 * questions — at the end. Progress is kept per person, so a module started
 * before service is still there after it.
 *
 * Two things keep it from rotting, which is what kills a staff handbook:
 *
 *   - A page may name drinks rather than repeat them. The page stores
 *     recipe ids or a tag; what it teaches is read off the book at the
 *     moment it is opened, so changing a recipe changes the training.
 *   - A question may be built from a drink rather than typed out, so
 *     "name what goes in this" is right by construction.
 *
 * Everything here is pure: no storage, no clock but the one it is handed,
 * no randomness but the one it is handed. `training.ts` in packages/api
 * stores it and packages/web draws it.
 */
/** The most pages one module may hold. Past this it is a manual, not a module. */
export const MAX_PAGES = 40;
/** The most questions one check may ask. */
export const MAX_QUESTIONS = 30;
/** The most choices one question may offer, the right answer included. */
export const MAX_CHOICES = 6;
export const MAX_TITLE = 120;
export const MAX_HEADING = 200;
/** A page of prose. Longer than this is two pages. */
export const MAX_BODY = 8000;
/** The default mark a check is passed at, as a percentage. */
export const DEFAULT_PASS_MARK = 80;
export const TRAINING_STATUSES = ["draft", "published", "retired"];
export function isTrainingStatus(value) {
    return value === "draft" || value === "published" || value === "retired";
}
/** The most attempts kept on a row. Enough to see a trend, not a log. */
export const MAX_ATTEMPTS = 10;
/**
 * Where somebody stands on a module.
 *
 * The check counts as one step beyond the last page, so a module read to
 * the end but not passed does not read as finished — which is the whole
 * reason there is a check.
 */
export function standingOf(module, progress) {
    const pages = module.pages.length;
    const steps = pages + (module.questions.length > 0 ? 1 : 0);
    if (!progress) {
        return { state: "not-started", read: 0, pages, through: 0, best: null };
    }
    const read = Math.max(0, Math.min(pages, progress.page));
    const best = progress.attempts.length === 0
        ? null
        : Math.max(...progress.attempts.map((a) => (a.asked === 0 ? 0 : (a.right / a.asked) * 100)));
    // A module with no questions is finished by reading it; one with
    // questions is finished by passing them.
    if (module.questions.length === 0) {
        const done = pages === 0 || read >= pages;
        return { state: done ? "passed" : read === 0 ? "not-started" : "reading", read, pages, through: steps === 0 ? 1 : read / steps, best };
    }
    if (progress.passedAt)
        return { state: "passed", read: pages, pages, through: 1, best };
    if (read >= pages)
        return { state: "check", read, pages, through: steps === 0 ? 1 : read / steps, best };
    return { state: read === 0 ? "not-started" : "reading", read, pages, through: steps === 0 ? 0 : read / steps, best };
}
/**
 * The course as a trainee sees it: published modules in the bar's own
 * order, each with where they have got to.
 *
 * Drafts are the author's business and retired ones are gone; a trainee is
 * shown what they are meant to work through and nothing else. `canEdit`
 * keeps the drafts in, because the person who wrote them needs to see them.
 */
export function courseOf(modules, progress, options = {}) {
    const byModule = new Map(progress.map((p) => [p.moduleId, p]));
    return modules
        .filter((m) => m.status === "published" || (options.canEdit === true && m.status === "draft"))
        .sort((a, b) => a.position - b.position || a.title.localeCompare(b.title))
        .map((module) => ({ module, standing: standingOf(module, byModule.get(module.moduleId)) }));
}
/** The next module to pick up: the first not yet passed. */
export function nextUp(rows) {
    return rows.find((r) => r.standing.state !== "passed") ?? null;
}
/** What a course adds up to, for the card at the top of the screen. */
export function courseStanding(rows) {
    const passed = rows.filter((r) => r.standing.state === "passed").length;
    const through = rows.length === 0 ? 0 : rows.reduce((sum, r) => sum + r.standing.through, 0) / rows.length;
    return { passed, total: rows.length, through };
}
/** What a page of drinks teaches, read off the book when it is opened. */
export function drinksOf(page, recipes, tagsOf) {
    const named = page.recipeIds
        .map((id) => recipes.find((r) => r.recipeId === id))
        .filter((r) => r !== undefined);
    if (!page.tag)
        return named;
    const tag = page.tag.toLowerCase();
    const tagged = recipes.filter((r) => tagsOf(r).some((t) => t.toLowerCase() === tag));
    // Named first, then whatever the tag adds, each drink once.
    const seen = new Set(named.map((r) => r.recipeId));
    return [...named, ...tagged.filter((r) => !seen.has(r.recipeId))];
}
/**
 * A shuffle that can be done twice and come out the same.
 *
 * This is what lets a check be marked on the server without the server
 * remembering anything: it hands out a paper with a seed, the trainee
 * sends the seed back with their answers, and the same paper is built
 * again to mark against. Mulberry32 — small, fast, and nobody is betting
 * on it.
 */
export function seededRng(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Fisher–Yates, on a copy. */
function shuffled(items, rng) {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        const a = out[i];
        out[i] = out[j];
        out[j] = a;
    }
    return out;
}
/**
 * A `build` question as it is put: the drink's name, and a list of
 * ingredients to pick from where the right ones are its own.
 *
 * The wrong answers come from the rest of the bar's shelf rather than from
 * a list somebody typed, so they are plausible — the point of the question
 * is telling Campari from Aperol, not Campari from a kitchen chair. A
 * recipe that is no longer in the book returns null, and the check skips
 * it, because asking what goes in a drink nobody can look up is a trick.
 */
export function buildQuestion(question, recipes, ingredients, nameOf, rng, decoys = 3) {
    const recipe = recipes.find((r) => r.recipeId === question.recipeId);
    if (!recipe || recipe.lines.length === 0)
        return null;
    const byId = new Map(ingredients.map((i) => [i.ingredientId, i.name]));
    const right = [...new Set(recipe.lines.map((l) => l.ingredientId))]
        .map((id) => byId.get(id))
        .filter((name) => name !== undefined);
    if (right.length === 0)
        return null;
    const inIt = new Set(right);
    const pool = ingredients.map((i) => i.name).filter((name) => !inIt.has(name));
    const wrong = shuffled(pool, rng).slice(0, decoys);
    const choices = shuffled([...right, ...wrong], rng);
    return {
        questionId: question.questionId,
        prompt: `What goes in a ${nameOf(recipe)}?`,
        choices,
        // A build question has several right answers; the index is the first,
        // and `markBuild` is what actually judges it.
        answer: choices.indexOf(right[0]),
        why: question.why,
    };
}
/** Which choices of a build question are the drink's own. */
export function rightChoices(question, recipes, ingredients) {
    const recipe = recipes.find((r) => r.recipeId === question.recipeId);
    if (!recipe)
        return [];
    const byId = new Map(ingredients.map((i) => [i.ingredientId, i.name]));
    return [...new Set(recipe.lines.map((l) => l.ingredientId))]
        .map((id) => byId.get(id))
        .filter((name) => name !== undefined);
}
/**
 * Build a check.
 *
 * Deterministic in `rng`, so the same seed gives the same paper: the
 * server builds it once to hand out and again to mark, and remembers
 * nothing in between. A `build` question whose drink has left the book is
 * dropped rather than asked, so a module outlives a deleted recipe.
 */
export function checkPaper(module, recipes, ingredients, nameOf, rng) {
    const asked = [];
    const right = {};
    for (const question of module.questions) {
        if (question.kind === "choice") {
            // Shuffled, so the answer is not always the one the author typed
            // first — and the paper differs between two people side by side.
            const order = shuffled(question.choices.map((text, at) => ({ text, at })), rng);
            asked.push({
                questionId: question.questionId,
                prompt: question.prompt,
                choices: order.map((c) => c.text),
                many: false,
                why: question.why,
            });
            right[question.questionId] = [order.findIndex((c) => c.at === question.answer)];
            continue;
        }
        const built = buildQuestion(question, recipes, ingredients, nameOf, rng);
        if (!built)
            continue;
        const want = new Set(rightChoices(question, recipes, ingredients));
        asked.push({
            questionId: built.questionId,
            prompt: built.prompt,
            choices: built.choices,
            many: true,
            why: built.why,
        });
        right[built.questionId] = built.choices
            .map((choice, at) => (want.has(choice) ? at : -1))
            .filter((at) => at >= 0);
    }
    return { asked, right };
}
/**
 * Mark a check.
 *
 * A question is right only when the answer is exactly right: every right
 * choice picked and no wrong one. Part marks would tell a bartender they
 * know a drink they have left the vermouth out of.
 */
export function markCheck(asked, answers, passMark) {
    const wrong = [];
    for (const question of asked) {
        const given = new Set(answers[question.questionId] ?? []);
        const want = new Set(question.right);
        const same = given.size === want.size && [...want].every((i) => given.has(i));
        if (!same)
            wrong.push(question.questionId);
    }
    const right = asked.length - wrong.length;
    const percent = asked.length === 0 ? 0 : (right / asked.length) * 100;
    return { right, asked: asked.length, percent, passed: asked.length > 0 && percent >= passMark, wrong };
}
/** An attempt recorded on the row, newest first and bounded. */
export function withAttempt(progress, attempt) {
    return {
        ...progress,
        attempts: [attempt, ...progress.attempts].slice(0, MAX_ATTEMPTS),
        passedAt: progress.passedAt ?? (attempt.passed ? attempt.at : null),
        updatedAt: attempt.at,
    };
}
//# sourceMappingURL=training.js.map